import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { Icon } from '@highlight-ui/icon';
import classnames from 'classnames';

import styles from './SelectContent.module.scss';

import useKeyNavigation from './hooks/useKeyNavigation';
import { ListBox } from '../ListBox';
import SearchInput from '../SearchInput/SearchInput';
import { SelectContext } from './context/SelectProvider';

import { SubmitFooter, AddOptionsFooter } from '../Footer';
import { DefaultEmptyState } from '../State/EmptyState';
import { Popover, PopoverContentProps } from '../Popover';

import SelectListBox from './SelectListBox';
import SelectSearchInput from './SelectSearchInput';

export const isScrollableElement = (el: HTMLElement) => {
  return el.clientHeight !== el.scrollHeight;
};

export type SelectContentProps = Pick<
  PopoverContentProps,
  'className' | 'metadata' | 'selectTriggerRef' | 'children'
>;

const SelectContent: React.FC<React.PropsWithChildren<SelectContentProps>> & {
  ListBox: typeof SelectListBox;
  SearchInput: typeof SelectSearchInput;
  DefaultEmptyState: typeof DefaultEmptyState;
  AddOptionsFooter: typeof AddOptionsFooter;
  SubmitFooter: typeof SubmitFooter;
} = ({ className, children, selectTriggerRef, ...props }) => {
  const listBoxRef = useRef<HTMLUListElement | null>(null);

  const {
    submitRef,
    isLoading,
    searchInputValue,
    shouldShowNoResultsLabel,
    closeOnSelect,
    selectedOptions,
    options,
    setIsVisible,
    isFullWidthVariant,
    optionType,
    hasFooter,
    setHasFooter,
    setHasSearch,
  } = useContext(SelectContext);

  const { handleKeyDown, handleSelect, handleSelectAll, removeFocusClass } =
    useKeyNavigation();

  useLayoutEffect(() => {
    const el = listBoxRef.current;

    if (!el) {
      return;
    }
    if (isScrollableElement(el)) {
      el.classList.add(styles.listboxOverflowScroll);
    } else {
      el.classList.remove(styles.listboxOverflowScroll);
    }
  }, [listBoxRef]);

  useEffect(() => {
    setHasFooter(false);
    setHasSearch(false);
    React.Children.map(children, (listItem) => {
      if (!React.isValidElement(listItem)) {
        return null;
      }

      if (
        (listItem as JSX.Element).type.displayName === SearchInput.displayName
      ) {
        setHasSearch(true);
        return;
      }

      switch (listItem.type) {
        case AddOptionsFooter:
        case SubmitFooter:
          setHasFooter(true);
          break;

        default:
          break;
      }
    });
  }, [children, setHasFooter, setHasSearch]);

  // hacky way to focus trigger once user uses "Escape" key on the content part
  const focusSelectTrigger = useCallback(() => {
    setTimeout(() => {
      if (selectTriggerRef?.current) {
        const triggerContainer: HTMLElement | null =
          selectTriggerRef.current.querySelector('[tabIndex="0"]');
        if (triggerContainer) triggerContainer?.focus();
      }
    }, 0);
  }, [selectTriggerRef]);

  const handleContentEscape = useCallback(() => {
    setTimeout(() => {
      setIsVisible(false);
    }, 0);

    focusSelectTrigger();

    if (submitRef.current) {
      submitRef.current.cancel();
    }
  }, [focusSelectTrigger, setIsVisible, submitRef]);

  const handleListBoxKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLLIElement>) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          handleContentEscape();
          break;

        case ' ':
          e.preventDefault();
          break;

        case 'Tab':
          removeFocusClass();
          if (!hasFooter && !e.shiftKey) {
            handleKeyDown(e);
          }
          break;

        case 'ArrowUp':
        case 'ArrowDown':
          focusSelectTrigger();
          handleKeyDown(e);
          break;

        default:
          handleKeyDown(e);
          break;
      }
    },
    [
      handleContentEscape,
      removeFocusClass,
      hasFooter,
      focusSelectTrigger,
      handleKeyDown,
    ],
  );

  const handleContentKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          handleContentEscape();
          break;

        case ' ':
          break;

        case 'Tab':
          removeFocusClass();
          if (!hasFooter && !e.shiftKey) {
            handleKeyDown(e);
          }
          break;

        default:
          handleKeyDown(e);
          break;
      }
    },
    [handleContentEscape, removeFocusClass, hasFooter, handleKeyDown],
  );

  const handleContentFooterKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>, isLastElement = false) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          handleContentEscape();
          break;

        case 'Tab':
          removeFocusClass();
          if (isLastElement && !e.shiftKey) {
            handleKeyDown(e);
          }
          break;

        case 'ArrowUp':
        case 'ArrowDown':
          focusSelectTrigger();
          handleKeyDown(e);
          break;

        default:
          break;
      }
    },
    [focusSelectTrigger, handleContentEscape, handleKeyDown, removeFocusClass],
  );

  const noOptions = options.length === 0;

  return (
    <Popover.Content
      {...(props as PopoverContentProps)}
      className={classnames(
        styles.list,
        {
          [styles.multiLineList]: optionType === 'multi-line',
          [styles.fullWidth]: isFullWidthVariant,
        },
        className,
      )}
    >
      {isLoading ? (
        <div className={styles.loadingContainer} aria-label="loading">
          <Icon
            name="spinner-third"
            size={16}
            spin
            color="icon-highlight"
            type="solid"
          />
        </div>
      ) : (
        React.Children.map(children, (listItem) => {
          if (!React.isValidElement(listItem)) {
            return null;
          }

          switch ((listItem as JSX.Element).type.displayName) {
            case SearchInput.displayName:
              return React.cloneElement(listItem as React.ReactElement, {
                onKeyDown: handleContentKeyDown,
              });

            case ListBox.displayName:
              return (
                !shouldShowNoResultsLabel &&
                !noOptions && (
                  <>
                    {!shouldShowNoResultsLabel &&
                      !noOptions &&
                      React.cloneElement(listItem as React.ReactElement, {
                        listRef: listBoxRef,
                        onKeyDown: handleListBoxKeyDown,
                        handleSelect,
                        handleSelectAll,
                        optionType,
                      })}
                  </>
                )
              );
            default:
              break;
          }

          switch (listItem.type) {
            case AddOptionsFooter:
              return React.cloneElement(listItem as React.ReactElement, {
                handleVisibility: () => setIsVisible(false),
                onKeyDown: handleContentFooterKeyDown,
                closeOnSelect,
              });

            case SubmitFooter:
              return React.cloneElement(listItem as React.ReactElement, {
                handleVisibility: () => setIsVisible(false),
                onKeyDown: handleContentFooterKeyDown,
                submitRef,
                selectedOptions,
              });

            case DefaultEmptyState:
              if (listItem.props.customComponent) {
                return listItem.props.customComponent();
              }

              return (
                <>
                  {shouldShowNoResultsLabel
                    ? React.cloneElement(listItem as React.ReactElement, {
                        label: searchInputValue,
                      })
                    : noOptions &&
                      React.cloneElement(listItem as React.ReactElement, {
                        searchNoResultsLabel: undefined,
                      })}
                </>
              );

            default:
              return null;
          }
        })
      )}
    </Popover.Content>
  );
};

SelectContent.displayName = 'Content';

SelectContent.SearchInput = SelectSearchInput;
SelectContent.DefaultEmptyState = DefaultEmptyState;
SelectContent.AddOptionsFooter = AddOptionsFooter;
SelectContent.SubmitFooter = SubmitFooter;
SelectContent.ListBox = SelectListBox;

export default SelectContent;
